import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pingBackend } from '../utils/warmup';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Music,
  Dumbbell,
  BookOpen,
  Mic2,
  Circle,
  Drum,
  ChevronRight,
  Star,
  Users,
  Award,
  Languages,
  Guitar,
  Sparkles,
  LayoutGrid
} from 'lucide-react';

const LandingPage = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  useEffect(() => { pingBackend(); }, []);

  const features = [
    { icon: Music, title: t('landing.features.chords.title'), desc: t('landing.features.chords.desc'), color: 'from-amber to-orange-500' },
    { icon: Dumbbell, title: t('landing.features.exercises.title'), desc: t('landing.features.exercises.desc'), color: 'from-blue to-cyan-400' },
    { icon: BookOpen, title: t('landing.features.theory.title'), desc: t('landing.features.theory.desc'), color: 'from-purple-500 to-pink-500' },
    { icon: Mic2, title: t('landing.features.tuner.title'), desc: t('landing.features.tuner.desc'), color: 'from-green-500 to-emerald-400' },
    { icon: Circle, title: t('landing.features.recorder.title'), desc: t('landing.features.recorder.desc'), color: 'from-red-500 to-rose-400' },
    { icon: Drum, title: t('landing.features.drums.title'), desc: t('landing.features.drums.desc'), color: 'from-yellow-500 to-amber' },
  ];

  const levels = [
    { level: t('levels.beginner'), desc: t('landing.levels.beginnerDesc'), icon: Star },
    { level: t('levels.intermediate'), desc: t('landing.levels.intermediateDesc'), icon: Users },
    { level: t('levels.expert'), desc: t('landing.levels.expertDesc'), icon: Award },
  ];

  const differentiators = [
    { icon: Languages,  title: t('landing.differentiators.italian.title'),    desc: t('landing.differentiators.italian.desc') },
    { icon: Guitar,     title: t('landing.differentiators.fingerstyle.title'), desc: t('landing.differentiators.fingerstyle.desc') },
    { icon: Sparkles,   title: t('landing.differentiators.maestro.title'),    desc: t('landing.differentiators.maestro.desc') },
    { icon: LayoutGrid, title: t('landing.differentiators.allInOne.title'),   desc: t('landing.differentiators.allInOne.desc') },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-16" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image with Gradient Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1510915361894-db8b60106cb1?w=1920&q=80)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-transparent to-[#0A0A0A]/50" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 mb-5 px-3 py-1 rounded-full bg-amber/10 border border-amber/30 text-amber text-xs font-bold uppercase tracking-widest">
                {t('landing.hero.tagline')}
              </span>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-tight mb-6">
                {t('landing.hero.title')}
              </h1>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-lg sm:text-xl text-zinc-400 mb-8 max-w-2xl"
            >
              {t('landing.hero.subtitle')}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link to={isAuthenticated ? '/dashboard' : '/auth'}>
                <Button 
                  size="lg" 
                  data-testid="hero-cta-btn"
                  className="bg-gradient-to-r from-amber to-orange-500 text-black font-bold text-lg px-8 py-6 rounded-full hover:scale-105 transition-transform shadow-glow"
                >
                  {t('landing.hero.cta')}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              {!isAuthenticated && (
                <Link to="/auth">
                  <Button 
                    size="lg" 
                    variant="outline"
                    data-testid="hero-login-btn"
                    className="border-white/20 text-white hover:bg-white/10 font-semibold text-lg px-8 py-6 rounded-full"
                  >
                    {t('landing.hero.ctaLogin')}
                  </Button>
                </Link>
              )}
            </motion.div>
          </div>
        </div>

        {/* Decorative Guitar Strings */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 hidden xl:flex flex-col gap-3 opacity-20">
          {[...Array(6)].map((_, i) => (
            <div 
              key={i} 
              className="h-[400px] bg-gradient-to-b from-transparent via-amber to-transparent"
              style={{ width: `${1 + i * 0.5}px` }}
            />
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl sm:text-5xl font-bold text-white text-center mb-16"
          >
            {t('landing.features.title')}
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative bg-[#121212] border border-white/5 rounded-2xl p-8 hover:border-amber/30 transition-colors card-spotlight"
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-7 h-7 text-black" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-zinc-400">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Differentiators Section — what makes FretFlow different */}
      <section className="py-24 px-6 md:px-12 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl sm:text-5xl font-bold text-white text-center mb-16"
          >
            {t('landing.differentiators.title')}
          </motion.h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {differentiators.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#121212] border border-amber/20 rounded-2xl p-6 hover:border-amber/50 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-amber/15 border border-amber/30 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-amber" />
                </div>
                <h3 className="font-heading text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Levels Section */}
      <section className="py-24 px-6 md:px-12 bg-gradient-to-b from-transparent via-amber/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl sm:text-5xl font-bold text-white text-center mb-16"
          >
            {t('landing.levels.title')}
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {levels.map((item, index) => (
              <motion.div
                key={item.level}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative group"
              >
                <div className="bg-[#121212] border border-white/5 rounded-2xl p-8 text-center hover:border-amber/30 transition-colors">
                  <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-amber/20 to-orange-500/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <item.icon className="w-10 h-10 text-amber" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-white mb-2">{item.level}</h3>
                  <p className="text-zinc-400">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-amber/10 to-orange-500/10 border border-amber/20 rounded-3xl p-12"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              {t('landing.finalCta.title')}
            </h2>
            <p className="text-zinc-400 mb-8 text-lg">
              {t('landing.finalCta.subtitle')}
            </p>
            <Link to={isAuthenticated ? '/dashboard' : '/auth'}>
              <Button 
                size="lg"
                data-testid="cta-final-btn"
                className="bg-gradient-to-r from-amber to-orange-500 text-black font-bold text-lg px-10 py-6 rounded-full hover:scale-105 transition-transform shadow-glow"
              >
                {t('landing.hero.cta')}
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber to-orange-500 flex items-center justify-center">
              <Music className="w-4 h-4 text-black" />
            </div>
            <span className="font-heading font-bold text-white">FretFlow</span>
          </div>
          <p className="text-zinc-500 text-sm">
            © {new Date().getFullYear()} FretFlow. {t('landing.footer.rights')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
